import React, { useEffect, useState } from "react";
import InstructorSidebar from "../common/instructorSidebar";
import InstructorSettingsLink from "../../Instructor/instructor-settings/settings-link/instructorSettingsLink";
import { createMenu, getMenus, MainMenu, updateMenu, updateMenuStatus } from "../../../api/setting";
import { Link } from "react-router-dom";

interface Menu {
    id: number;
    name: string;
    type: "normal" | "dropdown";
    link_type?: "internal" | "external";
    link?: string;
    order: number;
    is_active: boolean;
    children?: Menu[];
}

const SettingMainMenu: React.FC = () => {
    const [menus, setMenus] = useState<MainMenu[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchMenus = async () => {
        try {
            setLoading(true);
            const data = await getMenus();
            setMenus(data);
        } catch (error) {
            console.error("Failed to fetch menus", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMenus();
    }, []);

    const [showModal, setShowModal] = useState(false);
    const [newMenu, setNewMenu] = useState<Omit<Menu, "id">>({
        name: "",
        type: "normal",
        link_type: "internal",
        link: "",
        order: 0,
        is_active: true,
    });
    const [editingMenu, setEditingMenu] = useState<Menu | null>(null);

    const handleChange = async (id: number, field: "active", value: boolean) => {
        try {
            await updateMenuStatus(id, value)
            if (updateMenuStatus) {
                fetchMenus();
            }
        } catch (error) {
            console.error("Failed to update menu status", error)
            alert("Failed to update menu status")
        }
    }

    const handleSaveMenu = async () => {
        if (!newMenu.name.trim()) return alert("Menu name is required");

        if (newMenu.link_type === "external" && !/^https?:\/\//i.test(newMenu.link)) {
            alert("External link must start with http:// or https://");
            return;
        }

        try {
            if (editingMenu) {
                const updatedMenu = await updateMenu(editingMenu.id, newMenu);
                if (updatedMenu) fetchMenus();
            } else {
                const createdMenu = await createMenu(newMenu);
                if (createdMenu) fetchMenus();
            }

            setShowModal(false);
            setEditingMenu(null);
            setNewMenu({
                name: "",
                type: "normal",
                link_type: "internal",
                link: "",
                order: 0,
                is_active: true,
            });
        } catch (error) {
            console.error("Failed to create menu", error);
            alert("Failed to create menu. Check console for details.");
        }
    };

    const handleEditMenu = (menu: Menu) => {
        setEditingMenu(menu);
        setNewMenu({
            name: menu.name,
            type: menu.type,
            link_type: menu.link_type,
            link: menu.link,
            order: menu.order,
            is_active: menu.is_active,
            children: menu.children,
        });
        setShowModal(true);
    };

    const handleDeleteMenu = (id: number) => {
        if (window.confirm("Are you sure you want to delete this menu?")) {
            setMenus((prev) => prev.filter((menu) => menu.id !== id));
        }
    };

    return (
        <div className="content">
            <div className="container">
                <div className="row">
                    {/* Sidebar */}
                    <InstructorSidebar />
                    {/* /Sidebar */}

                    <div className="col-lg-9">
                        <div className="mb-3">
                            <h5>Settings</h5>
                        </div>

                        <InstructorSettingsLink />

                        {/* --- Main Menu Management Section --- */}
                        <div className="card mb-0 mt-4">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="card-title mb-0">
                                        Main Menu Management
                                    </h5>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => setShowModal(true)}
                                    >
                                        + Add Menu
                                    </button>
                                </div>

                                <table className="table align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ background: 'none' }}>Name</th>
                                            <th style={{ background: 'none' }}>Type</th>
                                            <th style={{ background: 'none' }}>Link Type</th>
                                            <th style={{ background: 'none' }}>Link</th>
                                            <th style={{ background: 'none' }}>Order</th>
                                            <th style={{ background: 'none' }}>Active</th>
                                            <th style={{ background: 'none' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={7} className="text-center">
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : menus.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="text-center">
                                                    No menus found
                                                </td>
                                            </tr>
                                        ) : (menus.map((menu) => (
                                            <tr key={menu.id}>
                                                <td>{menu.name}</td>
                                                <td className="text-capitalize">{menu.type}</td>
                                                <td className="text-capitalize">{menu.link_type || "-"}</td>
                                                <td>{menu.link || "-"}</td>
                                                <td>{menu.order}</td>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={menu.is_active}
                                                        onChange={(e) =>
                                                            handleChange(menu.id, "active", e.target.checked)
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    {menu.type === 'dropdown' && (
                                                        <Link className="btn btn-sm btn-success me-2" to={`/admin/settings/sub-menu/${menu.id}`}>
                                                            Dropdown
                                                        </Link>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-warning me-2"
                                                        onClick={() => handleEditMenu(menu)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleDeleteMenu(menu.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        )))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* --- Add Menu Modal --- */}
                        {showModal && (
                            <div
                                className="modal fade show"
                                style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
                            >
                                <div className="modal-dialog">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">
                                                {editingMenu ? "Edit Menu" : "Add New Menu"}
                                            </h5>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => {
                                                    setShowModal(false);
                                                    setEditingMenu(null);
                                                }}
                                            ></button>
                                        </div>
                                        <div className="modal-body">
                                            {/* Name */}
                                            <div className="mb-3">
                                                <label className="form-label">Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={newMenu.name}
                                                    onChange={(e) =>
                                                        setNewMenu({ ...newMenu, name: e.target.value })
                                                    }
                                                />
                                            </div>

                                            {/* Type */}
                                            <div className="mb-3">
                                                <label className="form-label">Type</label>
                                                <select
                                                    className="form-select"
                                                    value={newMenu.type}
                                                    onChange={(e) =>
                                                        setNewMenu({
                                                            ...newMenu,
                                                            type: e.target.value as "normal" | "dropdown",
                                                        })
                                                    }
                                                >
                                                    <option value="normal">Normal</option>
                                                    <option value="dropdown">Dropdown</option>
                                                </select>
                                            </div>

                                            {/* Link settings only for normal */}
                                            {newMenu.type === "normal" && (
                                                <>
                                                    <div className="mb-3">
                                                        <label className="form-label">Link Type</label>
                                                        <select
                                                            className="form-select"
                                                            value={newMenu.link_type}
                                                            onChange={(e) =>
                                                                setNewMenu({
                                                                    ...newMenu,
                                                                    link_type: e.target.value as "internal" | "external",
                                                                })
                                                            }
                                                        >
                                                            <option value="internal">Internal</option>
                                                            <option value="external">External</option>
                                                        </select>
                                                    </div>

                                                    <div className="mb-3">
                                                        <label className="form-label">Link</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={newMenu.link}
                                                            onChange={(e) => {
                                                                const value = e.target.value.trim();

                                                                // Detect if it's a full URL
                                                                const isExternal = /^https?:\/\//i.test(value);

                                                                setNewMenu((prev) => ({
                                                                    ...prev,
                                                                    link: value,
                                                                    // auto-change to external only if it matches and not already external
                                                                    link_type: isExternal ? "external" : prev.link_type,
                                                                }));
                                                            }}
                                                        />
                                                        <small className="text-muted">
                                                            Link type: <strong>{newMenu.link_type}</strong>
                                                        </small>
                                                    </div>
                                                </>
                                            )}

                                            {/* Order */}
                                            <div className="mb-3">
                                                <label className="form-label">Order</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={newMenu.order}
                                                    onChange={(e) =>
                                                        setNewMenu({ ...newMenu, order: Number(e.target.value) })
                                                    }
                                                />
                                            </div>

                                            {/* Active */}
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={newMenu.is_active}
                                                    onChange={(e) =>
                                                        setNewMenu({ ...newMenu, is_active: e.target.checked })
                                                    }
                                                />
                                                <label className="form-check-label">Active</label>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="ms-2 btn btn-secondary"
                                                onClick={() => {
                                                    setShowModal(false);
                                                    setEditingMenu(null);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="ms-2 btn btn-primary"
                                                onClick={handleSaveMenu}
                                            >
                                                Save Menu
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* --- End Modal --- */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingMainMenu;
